import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_CONFIG, apiUrl } from '@core/config/api.config';
import { SKIP_AUTH_INTERCEPTOR } from '@core/auth/auth.context';
import { ApiResponse, assertApiOk } from './api-response.model';

/** AutenticacionRequestDTO */
export interface AutenticacionRequest {
  correo: string;
  password: string;
}

/** AutenticacionResponseDTO */
export interface AutenticacionResponse {
  tokenJWT: string;
  username: string;
  role: string;
  nombreCompleto: string;
  permisos?: string[];
}

/** PerfilResponseDTO */
export interface PerfilResponse {
  idUsuario?: number;
  correo: string;
  estatus?: string;
  idPersona?: number;
  nombre?: string;
  apellidoPaterno?: string;
  apellidoMaterno?: string;
  roles?: string[];
  permisos?: string[];
}

export interface SolicitudRecuperacionRequest {
  correo: string;
}

export interface RestablecerPasswordRequest {
  token: string;
  nuevoPassword: string;
}

/**
 * Endpoints IAM de autenticación y recuperación.
 * Paths relativos a /auth/api
 */
@Injectable({ providedIn: 'root' })
export class AutenticacionService {
  private readonly http = inject(HttpClient);
  private readonly base = API_CONFIG.microservices.auth;

  private skipAuthContext(): { context: HttpContext } {
    return { context: new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true) };
  }

  private bearerHeaders(token: string): HttpHeaders {
    const value = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    return new HttpHeaders({ Authorization: value });
  }

  /** POST /auth/api/autenticacion/login */
  login(body: AutenticacionRequest): Observable<AutenticacionResponse> {
    return this.http
      .post<ApiResponse<AutenticacionResponse>>(
        apiUrl(this.base, 'autenticacion/login'),
        body,
        this.skipAuthContext()
      )
      .pipe(
        map((res) => {
          assertApiOk(res, 'No se pudo iniciar sesión.');
          const dto = res.dto;
          if (!dto?.tokenJWT) {
            throw new Error(res.mensaje?.trim() || 'Respuesta de login incompleta.');
          }
          return dto;
        })
      );
  }

  /** POST /auth/api/autenticacion/validar-token */
  validarToken(token: string): Observable<unknown> {
    return this.http
      .post<ApiResponse>(
        apiUrl(this.base, 'autenticacion/validar-token'),
        null,
        {
          headers: this.bearerHeaders(token),
          ...this.skipAuthContext()
        }
      )
      .pipe(
        map((res) => {
          assertApiOk(res, 'El token no es válido.');
          return res.dto ?? true;
        })
      );
  }

  /**
   * POST /auth/api/autenticacion/refrescar-token
   * El OpenAPI tipa dto como object; se aceptan formas comunes con tokenJWT.
   */
  refrescarToken(token: string): Observable<string> {
    return this.http
      .post<ApiResponse<AutenticacionResponse | { tokenJWT?: string } | string>>(
        apiUrl(this.base, 'autenticacion/refrescar-token'),
        null,
        {
          headers: this.bearerHeaders(token),
          ...this.skipAuthContext()
        }
      )
      .pipe(
        map((res) => {
          assertApiOk(res, 'No se pudo refrescar el token.');
          const next = extractTokenJwt(res.dto);
          if (!next) {
            throw new Error(res.mensaje?.trim() || 'No se recibió un token renovado.');
          }
          return next;
        })
      );
  }

  /** DELETE /auth/api/autenticacion/eliminar-token */
  eliminarToken(token: string): Observable<unknown> {
    return this.http
      .delete<ApiResponse>(apiUrl(this.base, 'autenticacion/eliminar-token'), {
        headers: this.bearerHeaders(token),
        ...this.skipAuthContext()
      })
      .pipe(
        map((res) => {
          assertApiOk(res, 'No se pudo cerrar la sesión en el servidor.');
          return res.dto ?? true;
        })
      );
  }

  /** GET /auth/api/usuarios/me */
  obtenerPerfil(token: string): Observable<PerfilResponse> {
    return this.http
      .get<ApiResponse<PerfilResponse>>(apiUrl(this.base, 'usuarios/me'), {
        headers: this.bearerHeaders(token),
        ...this.skipAuthContext()
      })
      .pipe(
        map((res) => {
          assertApiOk(res, 'No se pudo obtener el perfil.');
          if (!res.dto) {
            throw new Error(res.mensaje?.trim() || 'Perfil vacío.');
          }
          return res.dto;
        })
      );
  }

  /** POST /auth/api/recuperacion/solicitar */
  solicitarRecuperacion(body: SolicitudRecuperacionRequest): Observable<void> {
    return this.http
      .post<ApiResponse>(apiUrl(this.base, 'recuperacion/solicitar'), body, this.skipAuthContext())
      .pipe(
        map((res) => {
          assertApiOk(res, 'No se pudo solicitar la recuperación.');
        })
      );
  }

  /** POST /auth/api/recuperacion/restablecer */
  restablecerPassword(body: RestablecerPasswordRequest): Observable<void> {
    return this.http
      .post<ApiResponse>(apiUrl(this.base, 'recuperacion/restablecer'), body, this.skipAuthContext())
      .pipe(
        map((res) => {
          assertApiOk(res, 'No se pudo restablecer la contraseña.');
        })
      );
  }

  /** POST /auth/api/usuarios/activar-usuario?token= + contraseña inicial */
  activarUsuario(token: string, nuevoPassword: string): Observable<string> {
    const params = { token: token.trim() };
    return this.http
      .post<ApiResponse<string>>(
        apiUrl(this.base, 'usuarios/activar-usuario'),
        { nuevoPassword },
        { ...this.skipAuthContext(), params }
      )
      .pipe(
        map((res) => {
          assertApiOk(res, 'No se pudo activar la cuenta.');
          return res.mensaje?.trim() || (typeof res.dto === 'string' ? res.dto : '') || 'Cuenta activada.';
        })
      );
  }
}

function extractTokenJwt(dto: AutenticacionResponse | { tokenJWT?: string } | string | null | undefined): string | null {
  if (!dto) {
    return null;
  }
  if (typeof dto === 'string') {
    const trimmed = dto.trim();
    return trimmed || null;
  }
  if (typeof dto === 'object' && 'tokenJWT' in dto && typeof dto.tokenJWT === 'string') {
    const trimmed = dto.tokenJWT.trim();
    return trimmed || null;
  }
  return null;
}
